<template lang="pug">
.headerComponent(:class="[menuClass, headerClasses]")
  .inner
    .column.left.logoColumn
      .logoWrapper
        ui-logo(is-adaptive)

    .column.center
      .navigationWrapper
        app-navigation(
          @close-menu="closeMobileMenu"
          :is-mobile-menu-open="isMobileMenuOpened"
        )

    .column.right
      .subscriptionWrapper
        header-user-subscription

      .userWrapper
        header-user(
          :is-mobile-menu-open="isMobileMenuOpened"
        )

      .burger
        ui-svg-icon(
          :name="burgerIconName"
          :original-colors="true"
          @click="toggleMobileMenu"
        )
</template>

<script>
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import AppNavigation from './../AppNavigation/AppNavigation'
import HeaderUser from './HeaderUser/HeaderUser'
import useFlag from '@/_core/hooks/useFlag'
import useWindowBreakpointCallback from '@/_core/hooks/useWindowBreakpointCallback'
import HeaderUserSubscription from './HeaderUser/HeaderUserSubscription'

export default {
  components: {
    AppNavigation,
    HeaderUser,
    HeaderUserSubscription
  },
  inject: ['store'],
  setup () {
    const {
      flag: isMobileMenuOpened,
      toggle: toggleMobileMenu,
      off: closeMobileMenu,
      cssClass: menuClass
    } = useFlag('_opened', false)

    const burgerIconName = computed(() => (isMobileMenuOpened.value ? 'close' : 'burger'))

    const route = useRoute()
    const isHomepage = computed(() => route.name === 'Home')

    const handleOverflow = isOpened => {
      document.querySelector('body').style.overflow = isOpened ? 'hidden' : ''
      document.querySelector('#app').style.overflow = isOpened ? 'hidden' : ''
    }

    const headerClasses = computed(() => ({
      _homepage: isHomepage.value
    }))

    watch(isMobileMenuOpened, handleOverflow)

    useWindowBreakpointCallback(825, handleOverflow, closeMobileMenu, isMobileMenuOpened.value)

    return {
      isMobileMenuOpened,
      toggleMobileMenu,
      closeMobileMenu,
      menuClass,
      headerClasses,
      burgerIconName,
      isHomepage,
      route
    }
  }
}
</script>

<style lang="sass" scoped>
$headerHeight: 17.5*$u
$headerHeightPad: 16*$u
$headerHeightMobile: 15*$u

.headerComponent
  height: $headerHeight
  background-color: $cWhite

  > .inner
    max-width: $appMaxWidth
    height: 100%
    padding: 0 $appSidePaddingDesktop
    margin: 0 auto
    display: grid
    grid-template-columns: 1fr auto 1fr
    grid-column-gap: 2*$u

    > .column
      display: flex
      align-items: center
      &.left
        justify-content: flex-start
      &.center
        justify-content: center
      &.right
        justify-content: flex-end

    > .column
      > .logoWrapper
      > .navigationWrapper
      > .subscriptionWrapper
        margin-right: 5*$u
      > .userWrapper
        display: flex

    .burger
      display: none
      cursor: pointer
      width: 8*$u
      height: 8*$u
      flex: 0 0 8*$u
      flex-shrink: 0
      align-items: center
      justify-content: center
      @media (max-width: $padWidth)
        display: flex

  @media (max-width: $XLWidth)
    > .inner
      > .column
        > .navigationWrapper
          padding: 0 4*$u

  @media (max-width: $padWidth)
    height: $headerHeightPad
    > .inner
      padding: 0 $appSidePaddingMobile
    > .inner
      > .column
        > .navigationWrapper
          display: none

    :deep()
      .personal-account-button
        display: none

    &._opened
      width: 100vw
      height: 100vh
      position: fixed
      z-index: $zIndexMobileMenu + 100

      :deep()
        .personal-account-button
          display: flex

      > .inner
        height: 100%
        display: flex
        flex-flow: column
        background-color: $cWhite
        > .column
          justify-content: flex-start

          &.logoColumn
            flex: 0 0 $headerHeightPad

          > .navigationWrapper
            flex: 0
            display: flex
            flex-flow: column
            padding: 4*$u 0 6*$u 0

          &.center
            position: relative
            &:after
              content: ' '
              position: absolute
              height: 0.5*$u
              bottom: 0
              right: -$appSidePaddingDesktop
              left: -$appSidePaddingDesktop
              background-color: $cBackground

          &.right
            flex: 1
            align-items: flex-start
            overflow-y: auto
            overflow-x: hidden

          > .userWrapper
            flex: 1
            display: flex
            align-self: stretch

          > .subscriptionWrapper
            position: fixed
            right: 13*$u
            top: 2.5*$u
            z-index: 1000
            @media (max-width: $mobileWidth)
              top: 2*$u
            &:deep()
              .headerUserSubscriptionComponent
                > .button
                  position: relative
                  top: 2px

      .burger
        height: $headerHeightPad
        position: absolute
        top: 0
        right: $appSidePaddingMobile
        &:deep(.svgIconComponent)
          width: 4*$u !important
          height: 4*$u !important

        @media (max-width: $mobileWidth)
          height: $headerHeightMobile

  @media (max-width: $mobileWidth)
    height: $headerHeightMobile
    &._opened
      > .inner
        > .column
          &.logoColumn
            flex: 0 0 $headerHeightMobile

  &._homepage
    background: transparent
    @media (max-width: $XXLWidth)
      background: $cWhite
    .inner
      max-width: initial
      border-radius: $brM
      backdrop-filter: blur(8px)
      box-shadow: 0 4px 7px rgba(224, 222, 222, 0.25)
      background: rgba(255, 255, 255, 0.8)
      @media (max-width: $XXLWidth)
        border-radius: 0
        backdrop-filter: initial
</style>
